import { API_URL } from '../config/apiConfig';
import { fetchWithRetry } from '../utils/apiRetry';

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    if (!token) {
        throw new Error('No authentication token found');
    }
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
};

export const customSearch = async (startDate, endDate, projectIds, userId, status) => {
    try {
        // Ensure proper encoding of IDs
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        // Handle multiple project IDs
        if (projectIds && projectIds.length > 0) {
            projectIds.forEach(id => params.append('projectIds', id));
        }
        if (userId) params.append('userId', userId);
        if (status) params.append('status', status);

        const response = await fetchWithRetry(`${API_URL}/api/timecards/custom-search?${params}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });

        return await response.json();
    } catch (error) {
        console.error('Error in customSearch:', error);
        // Handle 503 Service Unavailable specifically
        if (error.status === 503) {
            throw new Error('Service temporarily unavailable. Please try again later.');
        }
        // Extract original error message if it exists
        if (error.response) {
            const errorData = await error.response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch custom search results');
        }
        throw error;
    }
};

export const exportCustomSearch = async (startDate, endDate, projectId, userId, status) => {
    try {
        // Ensure proper encoding of IDs
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        if (projectId) params.append('projectId', projectId);
        if (userId) params.append('userId', userId);
        if (status) params.append('status', status);

        const response = await fetchWithRetry(`${API_URL}/api/timecards/custom-search/export?${params}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Format dates for filename (convert YYYY-MM-DD to MM-DD-YYYY for readability)
        const formatDateForFilename = (dateStr) => {
            const [year, month, day] = dateStr.split('-');
            return `${month}-${day}-${year}`;
        };
        
        const formattedStartDate = formatDateForFilename(startDate);
        const formattedEndDate = formatDateForFilename(endDate);
        a.download = `custom-search-${formattedStartDate}_to_${formattedEndDate}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error in exportCustomSearch:', error);
        // Extract original error message if it exists
        if (error.response) {
            const errorData = await error.response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to export timecard data');
        }
        throw error;
    }
};

export const getQuickbooksReport = async (startDate, endDate) => {
    try {
        // Ensure proper encoding of dates
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);

        const response = await fetchWithRetry(`${API_URL}/api/timecards/quickbooks-report?${params}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });

        return await response.json();
    } catch (error) {
        console.error('Error in getQuickbooksReport:', error);
        // Extract original error message if it exists
        if (error.response) {
            const errorData = await error.response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch Quickbooks report data');
        }
        throw error;
    }
};

export const exportQuickbooksReport = async (startDate, endDate) => {
    try {
        // Ensure proper encoding of dates
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);

        const response = await fetchWithRetry(`${API_URL}/api/timecards/quickbooks-export?${params}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Get the current user's username from localStorage
        const token = localStorage.getItem('token');
        let username = 'user';
        if (token) {
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                if (payload.username) {
                    username = payload.username;
                }
            } catch (e) {
                console.error('Error extracting username from token:', e);
            }
        }
        a.download = `${username}_quickbooks-timecards-${startDate}_to_${endDate}.iif`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error in exportQuickbooksReport:', error);
        // Extract original error message if it exists
        if (error.response) {
            const errorData = await error.response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to export Quickbooks data');
        }
        throw error;
    }
};

export const getWeekTimecards = async (date) => {
    try {
        const response = await fetchWithRetry(`${API_URL}/api/timecards/week/${date}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });

        return await response.json();
    } catch (error) {
        console.error('Error in getWeekTimecards:', error);
        
        // Handle 404 responses
        if (error.status === 404 || error.message.includes('404') || error.message.includes('not found')) {
            return [];
        }
        
        // Extract original error message if it exists
        if (error.response) {
            const errorData = await error.response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to fetch weekly timecard data');
        }
        throw error;
    }
};

export const exportWeeklyTimecards = async (startDate, endDate) => {
    try {
        const response = await fetchWithRetry(
            `${API_URL}/api/timecards/report/weekly/export?startDate=${startDate}&endDate=${endDate}`,
            {
                method: 'GET',
                headers: getAuthHeaders()
            }
        );

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `weekly-timecard-report-${startDate}-${endDate}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error in exportWeeklyTimecards:', error);
        // Extract original error message if it exists
        if (error.response) {
            const errorData = await error.response.json().catch(() => null);
            throw new Error(errorData?.error || 'Failed to export weekly timecard data');
        }
        throw error;
    }
};
